import type { AppPermissionIdentifierWithoutWildcard } from "#interface/auth";

export const HelpEntryPdfMaxSizeBytes = 10 * 1024 * 1024;
export const HelpEntryPdfMaxSizeMb = HelpEntryPdfMaxSizeBytes / 1024 / 1024;
export const HelpEntryPdfMaxSizeMessage = `Help PDF files must be ${HelpEntryPdfMaxSizeMb} MB or smaller.`;

export const HelpSectionIds = [
  "home",
  "smartcell",
  "rfid",
  "production-job",
  "production-storage",
  "job-check",
  "power",
  "project",
  "iot",
  "kanban",
  "welding-params",
  "ncr",
  "mro-inventory",
  "machinery",
  "maintenance",
  "ghg",
  "software",
  "admin-cells",
  "admin-plants",
  "admin-machinery",
  "admin-site-features",
  "admin-users",
  "admin-invite-users",
  "admin-notifications",
  "account-settings",
] as const;

export type HelpSectionId = (typeof HelpSectionIds)[number];

export type HelpSectionDefinition = {
  id: HelpSectionId;
  label: string;
  routes: string[];
  routePrefixes: string[];
  viewPermission: AppPermissionIdentifierWithoutWildcard;
  managePermission: AppPermissionIdentifierWithoutWildcard;
};

export const HelpSections: readonly HelpSectionDefinition[] = [
  {
    id: "home",
    label: "Home",
    routes: ["/"],
    routePrefixes: [],
    viewPermission: "Admin::UpdateSelfProfile",
    managePermission: "Help::Home",
  },
  {
    id: "smartcell",
    label: "Smart Cell",
    routes: [],
    routePrefixes: ["/smartcell"],
    viewPermission: "SmartCell::ViewSmartCellOverview",
    managePermission: "Help::SmartCell",
  },
  {
    id: "rfid",
    label: "RFID Overview",
    routes: ["/rfid"],
    routePrefixes: ["/DeviceDetails"],
    viewPermission: "RFID::ViewRFIDOverview",
    managePermission: "Help::RFID",
  },
  {
    id: "production-job",
    label: "Jobs Overview",
    routes: [],
    routePrefixes: ["/JobOverview"],
    viewPermission: "ProductionJob::ViewJobOverview",
    managePermission: "Help::ProductionJob",
  },
  {
    id: "production-storage",
    label: "Storage",
    routes: ["/production-storage", "/production-storage-lookup"],
    routePrefixes: [],
    viewPermission: "ProductionJob::ViewJobStorage",
    managePermission: "Help::ProductionJob",
  },
  {
    id: "power",
    label: "Power",
    routes: ["/power"],
    routePrefixes: [],
    viewPermission: "SmartCell::ViewSmartCellOverview",
    managePermission: "Help::Power",
  },
  {
    id: "job-check",
    label: "Job Check",
    routes: ["/job-check"],
    routePrefixes: [],
    viewPermission: "ProductionJob::SearchTag",
    managePermission: "Help::ProductionJob",
  },
  {
    id: "project",
    label: "Project Overview",
    routes: [],
    routePrefixes: ["/ProjectOverview"],
    viewPermission: "ProductionJob::ViewProjectDashboard",
    managePermission: "Help::Project",
  },
  {
    id: "iot",
    label: "IOT Device Overview",
    routes: [],
    routePrefixes: ["/iot"],
    viewPermission: "IOT::ViewIOTOverview",
    managePermission: "Help::IOT",
  },
  {
    id: "kanban",
    label: "Kanban Overview",
    routes: [],
    routePrefixes: ["/kanban"],
    viewPermission: "Kanban::ViewKanbanOverview",
    managePermission: "Help::Kanban",
  },
  {
    id: "welding-params",
    label: "Welding Parameters",
    routes: [],
    routePrefixes: ["/welding"],
    viewPermission: "WeldingParams::ViewWeldingParams",
    managePermission: "Help::WeldingParams",
  },
  {
    id: "ncr",
    label: "Non-Conformance Reports",
    routes: [],
    routePrefixes: ["/ncr"],
    viewPermission: "Admin::ManageNCR",
    managePermission: "Help::NCR",
  },
  {
    id: "mro-inventory",
    label: "MRO Inventory",
    routes: [],
    routePrefixes: ["/inventory"],
    viewPermission: "Inventory::ViewInventory",
    managePermission: "Help::Inventory",
  },
  {
    id: "machinery",
    label: "Machinery",
    routes: [],
    routePrefixes: ["/machinery"],
    viewPermission: "Machinery::ViewFeature",
    managePermission: "Help::Machinery",
  },
  {
    id: "maintenance",
    label: "Maintenance Department",
    routes: [],
    routePrefixes: ["/maintenance"],
    viewPermission: "Admin::UpdateSelfProfile",
    managePermission: "Help::Maintenance",
  },
  {
    id: "software",
    label: "Software",
    routes: [],
    routePrefixes: [],
    viewPermission: "Admin::UpdateSelfProfile",
    managePermission: "Help::Software",
  },
  {
    id: "ghg",
    label: "Greenhouse Gas Emissions",
    routes: [],
    routePrefixes: ["/ghg"],
    viewPermission: "GHG::ViewGHG",
    managePermission: "Help::GHG",
  },
  {
    id: "admin-cells",
    label: "Manage Cells",
    routes: [],
    routePrefixes: ["/admin/cell"],
    viewPermission: "Admin::UpdateCell",
    managePermission: "Help::Admin",
  },
  {
    id: "admin-plants",
    label: "Manage Plants",
    routes: [],
    routePrefixes: ["/admin/plant"],
    viewPermission: "Admin::UpdatePlant",
    managePermission: "Help::Admin",
  },
  {
    id: "admin-machinery",
    label: "Manage Machinery",
    routes: [],
    routePrefixes: ["/admin/machinery"],
    viewPermission: "Admin::ManageMachinery",
    managePermission: "Help::Admin",
  },
  {
    id: "admin-site-features",
    label: "Manage Site Features",
    routes: ["/admin/TenantPermission"],
    routePrefixes: [],
    viewPermission: "Admin::UpdateTenantPermission",
    managePermission: "Help::Admin",
  },
  {
    id: "admin-users",
    label: "Manage Users",
    routes: [],
    routePrefixes: ["/admin/user"],
    viewPermission: "Admin::UpdateUser",
    managePermission: "Help::Admin",
  },
  {
    id: "admin-invite-users",
    label: "Invite Users",
    routes: ["/admin/invite-user"],
    routePrefixes: [],
    viewPermission: "Admin::InviteUser",
    managePermission: "Help::Admin",
  },
  {
    id: "admin-notifications",
    label: "Manage Notifications",
    routes: [],
    routePrefixes: ["/admin/notification"],
    viewPermission: "Admin::UpdateNotification",
    managePermission: "Help::Admin",
  },
  {
    id: "account-settings",
    label: "Account Settings",
    routes: [],
    routePrefixes: ["/settings/user"],
    viewPermission: "Admin::UpdateSelfProfile",
    managePermission: "Help::Account",
  },
] as const;

export type HelpEntry = {
  id: string;
  description: string;
  fileName: string;
  fileContentType: string;
  fileS3Key: string;
  createdAt: string;
  createdBy: string;
};

export type HelpPage = {
  section: HelpSectionId;
  title: string;
  entries: HelpEntry[];
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

export type HelpPagesValue = {
  pages: HelpPage[];
};
